import React, { useState } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  IconButton,
  Card,
  CardContent,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { resumeService } from '../../../services/resume';

const EducationForm = ({ resumeId, onError }) => {
  const [educations, setEducations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Education form
  const [educationForm, setEducationForm] = useState({
    degree: '',
    institutionName: '',
    fieldOfStudy: '',
    startDate: '',
    endDate: '',
    current: false,
    description: ''
  });

  const handleEducationChange = (field, value) => {
    setEducationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addEducation = async () => {
    if (!educationForm.degree || !educationForm.institutionName) {
      setError('Please fill in at least the degree and institution.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const educationData = {
        degree: educationForm.degree,
        institutionName: educationForm.institutionName,
        fieldOfStudy: educationForm.fieldOfStudy,
        startDate: educationForm.startDate,
        endDate: educationForm.current ? null : educationForm.endDate,
        description: educationForm.description
      };

      const response = await resumeService.createEducation(resumeId, educationData);
      
      // Add the new education to the local state
      setEducations(prev => [...prev, { ...educationData, id: response.id }]);
      
      // Reset form
      setEducationForm({
        degree: '',
        institutionName: '',
        fieldOfStudy: '',
        startDate: '',
        endDate: '',
        current: false,
        description: ''
      });

      setSuccess('Education added successfully!');
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      const errorMessage = `Failed to add education: ${error.message}`;
      setError(errorMessage);
      if (onError) onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const removeEducation = async (educationId) => {
    try {
      await resumeService.deleteEducation(resumeId, educationId);
      setEducations(prev => prev.filter(edu => edu.id !== educationId));
      setSuccess('Education removed successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      const errorMessage = `Failed to remove education: ${error.message}`;
      setError(errorMessage);
      if (onError) onError(errorMessage);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>Education</Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {/* Add Education Form */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Add New Education</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Degree"
              value={educationForm.degree}
              onChange={(e) => handleEducationChange('degree', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Institution"
              value={educationForm.institutionName}
              onChange={(e) => handleEducationChange('institutionName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Field of Study"
              value={educationForm.fieldOfStudy}
              onChange={(e) => handleEducationChange('fieldOfStudy', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={educationForm.startDate}
              onChange={(e) => handleEducationChange('startDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={educationForm.endDate}
              onChange={(e) => handleEducationChange('endDate', e.target.value)}
              disabled={educationForm.current}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={educationForm.description}
              onChange={(e) => handleEducationChange('description', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addEducation}
              disabled={loading || !educationForm.degree || !educationForm.institutionName}
            >
              {loading ? 'Adding...' : 'Add Education'}
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Existing Education */}
      {educations.map((edu) => (
        <Card key={edu.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">{edu.degree}</Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {edu.institutionName} • {edu.fieldOfStudy}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {edu.startDate} - {edu.current ? 'Present' : edu.endDate}
                </Typography>
                {edu.description && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {edu.description}
                  </Typography>
                )}
              </Box>
              <IconButton
                color="error"
                onClick={() => removeEducation(edu.id)}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Paper>
  );
};

export default EducationForm; 